import { useCallback, useMemo } from "react";
import { useLocalStorageArray } from "./useLocalStorage";
import { STORAGE_KEYS, CartItem } from "@/lib/localStorage";

/**
 * Shopping cart hook with localStorage persistence
 */
export function useShoppingCart() {
  const {
    items: cartItems,
    addItem,
    removeItem,
    updateItem,
    clearItems,
  } = useLocalStorageArray<CartItem>(STORAGE_KEYS.SHOPPING_CART, []);

  /**
   * Add product to cart or update quantity if already exists
   */
  const addToCart = useCallback(
    (product: Omit<CartItem, "quantity">, quantity: number = 1) => {
      const existingItemIndex = cartItems.findIndex(
        (item) => item.id === product.id,
      );

      if (existingItemIndex >= 0) {
        // Update quantity of existing item
        updateItem(
          (item) => item.id === product.id,
          (item) => ({ ...item, quantity: item.quantity + quantity }),
        );
      } else {
        // Add new item to cart
        addItem({ ...product, quantity });
      }
    },
    [cartItems, addItem, updateItem],
  );

  /**
   * Remove product from cart completely
   */
  const removeFromCart = useCallback(
    (productId: string) => {
      removeItem((item) => item.id === productId);
    },
    [removeItem],
  );

  /**
   * Update quantity of specific item
   */
  const updateQuantity = useCallback(
    (productId: string, quantity: number) => {
      if (quantity <= 0) {
        removeFromCart(productId);
      } else {
        updateItem(
          (item) => item.id === productId,
          (item) => ({ ...item, quantity }),
        );
      }
    },
    [updateItem, removeFromCart],
  );

  /**
   * Increase quantity by 1
   */
  const increaseQuantity = useCallback(
    (productId: string) => {
      updateItem(
        (item) => item.id === productId,
        (item) => ({ ...item, quantity: item.quantity + 1 }),
      );
    },
    [updateItem],
  );

  /**
   * Decrease quantity by 1
   */
  const decreaseQuantity = useCallback(
    (productId: string) => {
      updateItem(
        (item) => item.id === productId,
        (item) => ({ ...item, quantity: Math.max(1, item.quantity - 1) }),
      );
    },
    [updateItem],
  );

  /**
   * Clear entire cart
   */
  const clearCart = useCallback(() => {
    clearItems();
  }, [clearItems]);

  /**
   * Check if product is in cart
   */
  const isInCart = useCallback(
    (productId: string) => {
      return cartItems.some((item) => item.id === productId);
    },
    [cartItems],
  );

  /**
   * Get quantity of specific product in cart
   */
  const getItemQuantity = useCallback(
    (productId: string) => {
      const item = cartItems.find((item) => item.id === productId);
      return item?.quantity || 0;
    },
    [cartItems],
  );

  /**
   * Calculate cart totals
   */
  const cartTotals = useMemo(() => {
    const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    const totalPrice = cartItems.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0,
    );

    return {
      totalItems,
      totalPrice,
      itemCount: cartItems.length,
    };
  }, [cartItems]);

  /**
   * Get cart summary for display
   */
  const getCartSummary = useCallback(() => {
    return {
      items: cartItems,
      ...cartTotals,
      isEmpty: cartItems.length === 0,
    };
  }, [cartItems, cartTotals]);

  /**
   * Simulate checkout process
   */
  const checkout = useCallback(async () => {
    try {
      if (cartItems.length === 0) {
        throw new Error("Giỏ hàng trống");
      }

      // Mock checkout process
      const orderId = `order_${Date.now()}`;
      const orderData = {
        id: orderId,
        items: cartItems,
        total: cartTotals.totalPrice,
        createdAt: new Date().toISOString(),
        status: "pending",
      };

      // Store order in localStorage for order history
      const existingOrders = JSON.parse(
        localStorage.getItem("locuno_orders") || "[]",
      );
      localStorage.setItem(
        "locuno_orders",
        JSON.stringify([orderData, ...existingOrders]),
      );

      // Clear cart after successful checkout
      clearCart();

      return {
        success: true,
        orderId,
        message: "Đặt hàng thành công!",
      };
    } catch (error) {
      console.error("Checkout error:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Đặt hàng thất bại",
      };
    }
  }, [cartItems, cartTotals.totalPrice, clearCart]);

  return {
    // State
    cartItems,
    cartTotals,

    // Actions
    addToCart,
    removeFromCart,
    updateQuantity,
    increaseQuantity,
    decreaseQuantity,
    clearCart,

    // Queries
    isInCart,
    getItemQuantity,
    getCartSummary,

    // Operations
    checkout,
  };
}
